<template>
  <div class="shell" :class="[`mode-${mode}`]">
    <div class="backdrop" :class="[`mode-${mode}`]">
      <div class="modal" :class="[`mode-${mode}`]" :style="modalStyle">
        <div v-if="mode === 'modal'" class="top" :class="[`mode-${mode}`]">
          <div class="title">
            {{ title }}
          </div>
          <button v-if="shouldShowCloseButton" class="closeBtn" @click="$emit('close')" aria-label="Close">
            <AppIcon class="icon" name="close" />
          </button>
        </div>
        <div v-if="error" class="errorBanner" role="alert" aria-live="polite">
          <AppIcon class="errorBannerIcon" name="error" />
          <div class="errorBannerText">{{ error }}</div>
        </div>
        <div class="body" :class="[`mode-${mode}`]" :style="bodyStyle">
          <slot />
        </div>
        <div v-if="shouldRenderFooter" class="footer" :class="[`mode-${mode}`]">
          <div class="footer-start">
            <slot name="footer-start" />
          </div>
          <div class="footer-end">
            <slot name="footer">
              <button class="btn" @click="$emit('close')">Close</button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, useSlots } from 'vue'
import AppIcon from './AppIcon.vue'

const slots = useSlots()

const props = defineProps({
  title: { type: String, required: true },
  error: { type: String, default: '' },
  mode: { type: String, default: 'modal' },
  maxWidth: { type: [String, Number], default: 600 },
  modalOverflow: { type: String, default: '' },
  bodyOverflow: { type: String, default: '' },
  bodyMinHeight: { type: [String, Number], default: '' },
  bodyMaxHeight: { type: [String, Number], default: '' },
  embeddedFooter: { type: Boolean, default: true },
})

const emit = defineEmits(['close'])

const bodyStyle = computed(() => {
  const defaultOverflow = props.mode === 'embedded' ? 'hidden' : 'auto'
  const out = { overflow: String(props.bodyOverflow || defaultOverflow) }
  const mh = props.bodyMinHeight
  if (mh !== '' && mh != null) {
    out.minHeight = typeof mh === 'number' ? `${mh}px` : String(mh)
  }
  const max = props.bodyMaxHeight
  if (max !== '' && max != null) {
    out.maxHeight = typeof max === 'number' ? `${max}px` : String(max)
  }
  return out
})

const modalStyle = computed(() => {
  const defaultOverflow = props.mode === 'embedded' ? 'hidden' : 'visible'
  const out = { overflow: String(props.modalOverflow || defaultOverflow) }
  const mw = props.maxWidth
  if (mw !== '' && mw != null) {
    out.maxWidth = typeof mw === 'number' ? `${mw}px` : String(mw)
  }
  if (props.mode === 'embedded') {
    out.maxWidth = '100%'
    out.width = '100%'
  }
  return out
})

const shouldShowCloseButton = computed(() => props.mode === 'modal')

const shouldRenderFooter = computed(() => {
  if (props.mode === 'modal') return true
  return props.embeddedFooter && Boolean(slots.footer)
})

function handleEscape(e) {
  if (props.mode === 'modal' && e.key === 'Escape') {
    emit('close')
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
})
</script>

<style scoped>
.shell {
  min-width: 0;
}

.shell.mode-embedded {
  height: 100%;
  min-height: 0;
}

.backdrop {
  position: fixed;
  inset: 0;
  background: var(--backdrop);
  display: grid;
  place-items: center;
  padding: 18px;
  z-index: 50;
}

.backdrop.mode-embedded {
  position: static;
  inset: auto;
  background: transparent;
  display: block;
  padding: 0;
  z-index: auto;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.modal {
  width: 100%;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 10px 30px var(--shadow);
  max-height: calc(100vh - 36px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal.mode-embedded {
  max-height: none;
  height: 100%;
  min-height: 0;
  box-shadow: none;
  border: none;
  border-radius: 0;
}

.top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border);
}

.top.mode-embedded {
  padding: 0 0 14px;
}

.errorBanner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 16px 24px 0;
  padding: 10px 12px;
  border: 1px solid color-mix(in srgb, var(--danger) 28%, var(--border));
  border-radius: 10px;
  background: color-mix(in srgb, var(--danger) 12%, var(--surface));
  color: var(--text);
}

.errorBannerIcon {
  width: 18px;
  height: 18px;
  color: var(--danger);
  flex: 0 0 auto;
  margin-top: 1px;
}

.errorBannerText {
  min-width: 0;
  font-size: 13px;
  line-height: 1.4;
}

.title {
  font-size: 20px;
  font-weight: 600;
  flex: 1;
}

.closeBtn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: var(--text-muted);
}

.closeBtn:hover {
  background: var(--row-hover-bg);
  color: var(--text);
}

.icon {
  width: 20px;
  height: 20px;
}

.body {
  padding: 24px;
  overflow: auto;
  flex: 1 1 auto;
}

.body.mode-embedded {
  padding: 14px;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.body :deep(.dialogFormGrid) {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.body :deep(.dialogFormField) {
  min-width: 0;
}

.body :deep(.dialogFormFieldSpan2) {
  grid-column: span 2;
}

.body :deep(.dialogFormLabel) {
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 6px;
}

.body :deep(.dialogFormInputFull) {
  width: 100%;
}

.body :deep(.dialogFormControlRow) {
  display: flex;
  align-items: center;
  min-height: 42px;
}

.body :deep(.dialogFormIconAlignTop) {
  align-items: flex-start;
  padding-top: 2px;
}

.body :deep(.dialogHelperText) {
  margin-top: 6px;
  font-size: 12px;
  color: var(--muted);
}

.body :deep(.dialogOutputText) {
  min-height: 24px;
  padding: 2px 0;
  color: var(--text);
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.body :deep(.dialogFormTextarea) {
  width: 100%;
  min-height: 140px;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
  resize: vertical;
  background: var(--field-bg);
  color: var(--text);
}

.body :deep(.dialogFormTextareaLg) {
  min-height: 180px;
}

.footer {
  padding: 16px 24px;
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.footer-start {
  display: flex;
  gap: 8px;
  align-items: center;
}

.footer-start:empty {
  display: none;
}

.footer-end {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-left: auto;
}

.footer.mode-embedded {
  padding: 14px;
  flex: 0 0 auto;
}

.footer :deep(.modalFooterShell) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.footer :deep(.modalFooterRow) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.footer :deep(.modalFooterDanger) {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.footer :deep(.modalFooterActions) {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}

.footer :deep(.btn),
.btn {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--border);
}

.footer :deep(.btn:not(.primary):not(.danger)),
.btn {
  background: var(--surface);
  color: var(--text);
}

.footer :deep(.btn:not(.primary):not(.danger):hover),
.btn:hover {
  background: var(--row-hover-bg);
}
</style>
